import { useState, useEffect } from "react";
import type { Structure, Beneficiary, BeneficiaryDistribution, TrustLossLedgerEntry, StructureDocument, InvestorProfile, HouseholdMember, ExcludedEntityType } from "../../types/entities";
import { calculateEffectiveRate, calculateMarginalRate } from "../../lib/calculations";
import {
  resolveBeneficiaryIncome,
  resolveBeneficiaryMedicareExempt,
  isBeneficiaryLinked,
} from "../../lib/beneficiaryResolution";
import { supabase, isSupabaseEnabled } from "../../lib/supabase";
import { uploadToStorage, downloadFromStorage, deleteFromStorage } from "../../lib/userStorage";
import { migrateStructureDocs } from "../../lib/docMigration";
import { normalizeFY } from "../../lib/trustLossCarryForward";
import { NumberInput } from "../ui/NumberInput";
import { InfoTooltip } from "../ui/InfoTooltip";

// B106 — entity_type narrowed to the 4-token union. "Personal" is no
// longer an entity type — personal-name properties have structureId IS
// NULL and never reach this modal. See LESSONS L40.
const ENTITY_TYPES = [
  "Trust",
  "SMSF",
  "Company",
  "Company / Trust",
] as const;

// B106 — Trust sub-classification moved to structures.trust_type column.
// Migration `20260502_remove_personal_entity_type.sql` back-fills these
// from the legacy verbose entity_type values.
const TRUST_SUBTYPES = [
  "Discretionary trust",
  "Discretionary trust with corporate trustee",
  "Unit trust",
  "Bare trust",
  "Hybrid trust",
  "Testamentary trust",
] as const;

const AU_STATES = ["NSW", "VIC", "QLD", "WA", "SA", "TAS", "ACT", "NT"];

/**
 * Relationship options for trust/SMSF/company beneficiary rows.
 *
 * B119 sibling UX (Day 55) — "Self" added at first position so the primary
 * user beneficiary renders correctly when added via "Add from profile".
 * Pre-fix prod data correctly stored `relationship: "Self"` for the primary
 * but the select's option list omitted "Self", so the rendered control fell
 * back to its first option ("Child"). On save the form would have corrupted
 * the DB value Self → Child. Audit doc: docs/audits/B119-audit.md § Sibling
 * UX follow-up.
 */
export const RELATIONSHIP_OPTIONS = ["Self", "Spouse", "Child", "Parent", "Sibling", "Other"] as const;

/**
 * Pure helper: return the canonical default relationship label for a
 * household member when they're added to a structure as a beneficiary.
 *
 * - `isPrimary === true` → "Self" (the primary user is themselves).
 * - `member` has a spouse-type relationship pointing at "primary"
 *   (`type === "spouse" | "partner"`) → "Spouse".
 * - Otherwise → "" (the user must pick from the dropdown; a placeholder
 *   option is rendered while value is empty).
 *
 * Returns "" when `member` is null (defensive — caller passes null when
 * the picker yields no resolvable household member).
 */
export function defaultRelationshipForMember(
  member: HouseholdMember | null,
  isPrimary: boolean,
): string {
  if (isPrimary) return "Self";
  if (!member) return "";
  const rels = member.relationships ?? [];
  const spouseRel = rels.find(
    (r) => r.toMemberId === "primary" && (r.type === "spouse" || r.type === "partner"),
  );
  if (spouseRel) return "Spouse";
  return "";
}

const DOCUMENT_LABELS = [
  "Trust deed / Company constitution / SMSF trust deed",
  "ABN registration",
  "GST registration",
  "Other",
];

function uid() {
  return crypto.randomUUID();
}

/** B121 — current FY label in canonical "YYYY-YY" form (e.g. "2024-25"). */
function currentFY(): string {
  const now = new Date();
  const startYear = now.getMonth() >= 6 ? now.getFullYear() : now.getFullYear() - 1;
  return `${startYear}-${String(startYear + 1).slice(2)}`;
}

/** B121 — prior-FY entries (sorted newest-first) for history collapsible. */
function priorFYs(distributions: BeneficiaryDistribution[] | undefined): BeneficiaryDistribution[] {
  if (!distributions) return [];
  const cur = currentFY();
  return distributions.filter((d) => d.fy !== cur).sort((a, b) => b.fy.localeCompare(a.fy));
}

/** B125 — empty trust-loss ledger entry seed. */
function emptyLedgerEntry(fy: string): TrustLossLedgerEntry {
  return { fy, taxLoss: 0, applied: {}, remaining: 0 };
}

/**
 * B146 (Day 80) — Generate prior-N FY options for the loss carry-forward
 * picker. Excludes current FY (current-year losses are lodged at FY-end,
 * not carried forward). Includes any pre-existing entry FYs verbatim so
 * the user can see legacy data even if non-canonical.
 *
 * Returns array of {value, label, disabled} for the <select>.
 */
function generateLedgerFYOptions(
  curFY: string,
  existingEntries: TrustLossLedgerEntry[],
  priorN: number = 10,
): Array<{ value: string; label: string; disabled: boolean }> {
  const startYear = parseInt(curFY.slice(0, 4), 10);
  const opts: Array<{ value: string; label: string; disabled: boolean }> = [];
  // B146 follow-up — duplicate detection across FY format variants.
  // Normalise existing entries' fy strings so a legacy "FY23-24" entry
  // disables the canonical "2023-24" option in the picker (preventing the
  // user from creating two entries that FIFO-apply to the same FY).
  const usedFYsNormalised = new Set(existingEntries.map((e) => normalizeFY(e.fy)));

  for (let i = 1; i <= priorN; i++) {
    const y = startYear - i;
    const fy = `${y}-${String(y + 1).slice(2)}`;
    opts.push({
      value: fy,
      label: fy,
      disabled: usedFYsNormalised.has(fy), // fy is already canonical
    });
  }

  // Include any pre-existing entries that aren't in the prior-N range
  // (older than priorN, or non-canonical format). Show with verbatim label.
  for (const entry of existingEntries) {
    if (!opts.find((o) => o.value === entry.fy)) {
      opts.push({
        value: entry.fy,
        label: `${entry.fy} (existing)`,
        disabled: false,
      });
    }
  }

  return opts;
}

function isTrust(entityType: string) {
  // B106 — narrowed: Trust is a single entity_type value. Sub-classification
  // (Discretionary / Unit / Bare etc.) lives in trust_type.
  return entityType === "Trust" || entityType === "Company / Trust";
}

function isTrustOrSMSF(entityType: string) {
  return isTrust(entityType) || entityType === "SMSF";
}

function isCompanyLike(entityType: string) {
  return entityType === "Company" || entityType === "Company / Trust";
}

function blankStructure(): Structure {
  return {
    id: uid(),
    // B106 — Option B: no pre-selected entity type. Save is disabled until
    // the user explicitly picks. Forces a deliberate choice rather than
    // letting the silent default class of issue B106 was about reappear.
    entityType: "" as Structure["entityType"],
    displayName: "",
    trusteeName: "",
    trusteeType: "Individual",
    companyName: "",
    trustName: "",
    trustType: "",
    abn: "",
    tfn: "",
    acn: "",
    gstRegistered: false,
    dateEstablished: "",
    stateOfEstablishment: "",
    registeredOfficeAddress: "",
    memberNames: [],
    beneficiaries: [],
    documents: [],
  };
}

type ProfilePerson = {
  id: string;
  displayName: string;
  income: number;
  medicareExempt: boolean;
};

export default function StructureModal({
  structure,
  profile,
  onSave,
  onClose,
}: {
  structure: Structure | null;
  profile?: InvestorProfile;
  onSave: (s: Structure) => void;
  onClose: () => void;
}) {
  const [draft, setDraft] = useState<Structure>(() =>
    structure ? { ...structure } : blankStructure()
  );
  const [showProfilePicker, setShowProfilePicker] = useState(false);
  const [pickerValue, setPickerValue] = useState("");
  const [docUploading, setDocUploading] = useState<string | null>(null);
  const [docUploadError, setDocUploadError] = useState<string | null>(null);

  // ── Document migration on mount (detect stale blob: URLs from pre-B23) ──
  useEffect(() => {
    const migrated = migrateStructureDocs(draft.documents ?? []);
    if (migrated.changed) {
      const updated = { ...draft, documents: migrated.documents };
      setDraft(updated);
      onSave(updated);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const set = <K extends keyof Structure>(key: K, value: Structure[K]) =>
    setDraft((p) => ({ ...p, [key]: value }));

  // ── Beneficiaries ─────────────────────────────────────────────────────────
  const beneficiaries: Beneficiary[] = draft.beneficiaries ?? [];
  const totalDistPct = beneficiaries.reduce(
    (s, b) => {
      // B121 — sum-validation operates on the resolved current-FY percent
      // (distributions[] entry if present; legacy fallback otherwise).
      const cur = currentFY();
      const fyEntry = b.distributions?.find((d) => d.fy === cur);
      const percent = fyEntry?.type === "percent" ? fyEntry.value
        : fyEntry?.type === "amount" ? 0  // amount-type contributes $ not %
        : (b.distributionPercent ?? 0);
      return s + percent;
    },
    0
  );

  // Build list of people from profile for "Add from profile"
  const profilePeople: ProfilePerson[] = [];
  if (profile) {
    const primaryName = profile.preferredName || profile.fullLegalName || "";
    if (primaryName) {
      profilePeople.push({
        id: "primary",
        displayName: primaryName,
        income: profile.annualPreTaxIncome ?? 0,
        medicareExempt: profile.medicareExempt ?? false,
      });
    }
    (profile.householdMembers ?? []).forEach((m) => {
      const name = m.preferredName || m.fullLegalName;
      if (name) {
        profilePeople.push({
          id: m.id,
          displayName: name,
          income: m.annualPreTaxIncome ?? 0,
          medicareExempt: m.medicareExempt ?? false,
        });
      }
    });
  }

  function addBeneficiary() {
    set("beneficiaries", [
      ...beneficiaries,
      { id: uid(), name: "", relationship: "Child", distributions: [], notes: "" },
    ]);
  }

  function addBeneficiaryFromProfile(person: ProfilePerson) {
    // B119 sibling UX (Day 55) — auto-default the relationship so the
    // primary user is correctly tagged as "Self", spouses as "Spouse",
    // and other household members are left for the user to pick (empty
    // value triggers the placeholder option in the rendered <select>).
    const isPrimary = person.id === "primary";
    const member = isPrimary
      ? null
      : (profile?.householdMembers ?? []).find((m) => m.id === person.id) ?? null;
    const relationship = defaultRelationshipForMember(member, isPrimary);
    set("beneficiaries", [
      ...beneficiaries,
      {
        id: uid(),
        name: person.displayName,
        relationship,
        distributions: [],
        notes: "",
        // B132 (Day 55) — DO NOT seed annualIncome / medicareExempt for
        // linked beneficiaries. The resolver helpers
        // `resolveBeneficiaryIncome` / `resolveBeneficiaryMedicareExempt`
        // read live from `profile` / `profile.householdMembers` at render
        // time, so duplicating the value here would only re-introduce the
        // staleness B132 fixes. Manual-add path (the `addBeneficiary`
        // function above) keeps inline `annualIncome` because manual-add
        // beneficiaries aren't profile entities.
        // B103 Phase 1 — preserve the link back to the household member so
        // future household-scope aggregation can identify trust beneficiaries
        // who are part of the household. "primary" means the main investor.
        householdMemberId: person.id,
      },
    ]);
    setShowProfilePicker(false);
    setPickerValue("");
  }

  function updateBeneficiary(id: string, field: keyof Beneficiary, value: string | number | boolean) {
    set(
      "beneficiaries",
      beneficiaries.map((b) => {
        if (b.id !== id) return b;
        if (field === "annualIncome" || field === "bucketCompanyTaxRate") return { ...b, [field]: Number(value) };
        if (field === "distributionPercent") return { ...b, [field]: Number(value) };
        if (field === "distributions") return { ...b, distributions: value as unknown as BeneficiaryDistribution[] };
        if (field === "medicareExempt" || field === "isBucketCompany") return { ...b, [field]: Boolean(value) };
        return { ...b, [field]: value };
      })
    );
  }

  function removeBeneficiary(id: string) {
    set("beneficiaries", beneficiaries.filter((b) => b.id !== id));
  }

  // ── Member names (SMSF) ───────────────────────────────────────────────────
  const memberNames: string[] = draft.memberNames ?? [];

  function addMember() {
    set("memberNames", [...memberNames, ""]);
  }

  function updateMember(index: number, value: string) {
    const updated = [...memberNames];
    updated[index] = value;
    set("memberNames", updated);
  }

  function removeMember(index: number) {
    set("memberNames", memberNames.filter((_, i) => i !== index));
  }

  // ── Documents (B23 — Supabase Storage backed, replaces blob: URL anti-pattern) ──
  const documents: StructureDocument[] = draft.documents ?? [];

  async function uploadStructureDoc(label: string, file: File, replacingDocId?: string) {
    if (!isSupabaseEnabled() || !supabase) {
      setDocUploadError("Storage unavailable");
      return;
    }
    const slotKey = replacingDocId ?? `new:${label}`;
    setDocUploading(slotKey);
    setDocUploadError(null);
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      setDocUploadError("Not signed in");
      setDocUploading(null);
      return;
    }
    // Path: {userId}/structures/{structureId}/{filename}
    const pathPrefix = `${user.id}/structures/${draft.id}`;
    const result = await uploadToStorage("profile-documents", pathPrefix, file);
    setDocUploading(null);
    if (!result.ok) {
      setDocUploadError(result.error);
      return;
    }
    // If replacing an existing doc, delete the old storage object first (best-effort)
    let nextDocs: StructureDocument[];
    if (replacingDocId) {
      const existing = documents.find((d) => d.id === replacingDocId);
      if (existing?.filePath) await deleteFromStorage("profile-documents", existing.filePath);
      nextDocs = documents.map((d) => d.id === replacingDocId ? {
        ...d,
        filename: file.name,
        type: file.type,
        dateUploaded: new Date().toISOString().slice(0, 10),
        filePath: result.result.filePath,
        fileSize: result.result.fileSize,
        needsReupload: false,
        url: undefined,
      } : d);
    } else {
      const doc: StructureDocument = {
        id: uid(),
        filename: file.name,
        type: file.type,
        dateUploaded: new Date().toISOString().slice(0, 10),
        filePath: result.result.filePath,
        fileSize: result.result.fileSize,
        needsReupload: false,
        label,
      };
      nextDocs = [...documents, doc];
    }
    set("documents", nextDocs);
  }

  async function removeStructureDoc(docId: string) {
    const existing = documents.find((d) => d.id === docId);
    if (!existing) return;
    if (existing.filePath) await deleteFromStorage("profile-documents", existing.filePath);
    set("documents", documents.filter((d) => d.id !== docId));
  }

  // ── Save ──────────────────────────────────────────────────────────────────
  function handleSave() {
    if (!draft.displayName.trim()) {
      alert("Display name is required.");
      return;
    }
    if (!draft.entityType) {
      alert("Choose an entity type to continue.");
      return;
    }
    onSave(draft);
    onClose();
  }

  // ── Helpers ───────────────────────────────────────────────────────────────
  const et = draft.entityType;
  // B106 — Option B: Save disabled until entityType picked.
  const canSave = !!draft.displayName.trim() && !!et;
  const showTrustFields = isTrustOrSMSF(et);
  const showCompanyFields = isCompanyLike(et);
  const showBeneficiaries = isTrustOrSMSF(et);
  const showSMSFMembers = et === "SMSF";
  // Personal-name properties never reach this modal post-B106; the legacy
  // `!isPersonal` field-visibility guards become "show fields when an
  // entity type is picked" — gate on `et` truthiness.
  const showEntityFields = !!et;

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 py-8">
      <div className="mx-4 w-full max-w-2xl rounded-2xl bg-white p-6 shadow-2xl">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-neutral-900">
            {structure ? "Edit structure" : "Add structure"}
          </h2>
          <button
            onClick={onClose}
            className="rounded-lg px-2 py-1 text-neutral-400 hover:bg-neutral-100"
          >
            ✕
          </button>
        </div>

        <div className="mt-5 space-y-4">
          {/* ── Core fields ─────────────────────────────────────────────── */}
          <div>
            <label className="block text-xs font-medium text-neutral-500 mb-1">
              Display name <span className="text-red-500">*</span>
            </label>
            <input
              className="w-full rounded-xl border border-neutral-200 px-3 py-2 text-sm"
              value={draft.displayName}
              onChange={(e) => set("displayName", e.target.value)}
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-neutral-500 mb-1">
              Entity type <span className="text-red-500">*</span>
            </label>
            <select
              className="w-full rounded-xl border border-neutral-200 px-3 py-2 text-sm"
              value={draft.entityType}
              onChange={(e) => set("entityType", e.target.value as Structure["entityType"])}
            >
              <option value="">— Choose entity type —</option>
              {ENTITY_TYPES.map((t) => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
            {!et && (
              <p className="mt-1 text-xs text-neutral-500">Choose an entity type to continue.</p>
            )}
          </div>

          {/* B106 — Trust sub-classification. The legal distinction between
              Discretionary / Discretionary-with-corporate-trustee / Unit /
              Bare matters (different ASIC + Div 7A treatment). Stored on
              structures.trust_type per the migration. */}
          {isTrust(et) && (
            <div>
              <label className="block text-xs font-medium text-neutral-500 mb-1">Trust sub-type</label>
              <select
                className="w-full rounded-xl border border-neutral-200 px-3 py-2 text-sm"
                value={draft.trustType}
                onChange={(e) => set("trustType", e.target.value)}
              >
                <option value="">— Choose sub-type (optional) —</option>
                {TRUST_SUBTYPES.map((t) => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
            </div>
          )}

          {/* B130 Wave 2C Phase 4 — ITAA s40-27(2) excluded-entity flag.
              Drives the Div 40 second-hand-rule exception. NOT applicable
              to individuals / discretionary trusts / family trusts / SMSFs /
              partnerships of individuals — those leave it as "Not excluded"
              and second-hand assets remain blocked in post-9-May-2017
              residential property. */}
          {showEntityFields && (
            <div>
              <label className="flex items-center gap-1 text-xs font-medium text-neutral-500 mb-1">
                ITAA s40-27 Excluded Entity
                <InfoTooltip tip="Tick this only if the entity is a company, public unit trust, MIT, or APRA-regulated super fund. SMSFs, individuals, discretionary trusts, and family trusts are NOT excluded entities — leave as 'Not excluded'. Drives the Div 40 second-hand-rule exception." />
              </label>
              <select
                className="w-full rounded-xl border border-neutral-200 px-3 py-2 text-sm"
                value={draft.excludedEntityType ?? ""}
                onChange={(e) => {
                  const v = e.target.value;
                  set(
                    "excludedEntityType",
                    v === "" ? null : (v as ExcludedEntityType),
                  );
                }}
              >
                <option value="">Not excluded (default)</option>
                <option value="company">Company</option>
                <option value="public-unit-trust">Public unit trust</option>
                <option value="MIT">Managed Investment Trust (MIT)</option>
                <option value="APRA-super">APRA-regulated super fund</option>
              </select>
              {draft.excludedEntityType && (
                <p className="mt-1 text-xs text-neutral-500">
                  Div 40 second-hand-rule exception will apply to assets in
                  residential property owned by this entity.
                </p>
              )}
            </div>
          )}

          {showEntityFields && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-neutral-500 mb-1">ABN</label>
                <input
                  className="w-full rounded-xl border border-neutral-200 px-3 py-2 text-sm"
                  value={draft.abn}
                  onChange={(e) => set("abn", e.target.value)}
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-neutral-500 mb-1">TFN</label>
                <input
                  className="w-full rounded-xl border border-neutral-200 px-3 py-2 text-sm"
                  value={draft.tfn}
                  onChange={(e) => set("tfn", e.target.value)}
                />
              </div>
            </div>
          )}

          {showEntityFields && (
            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                id="gst"
                checked={draft.gstRegistered ?? false}
                onChange={(e) => set("gstRegistered", e.target.checked)}
                className="h-4 w-4 rounded border-neutral-300"
              />
              <label htmlFor="gst" className="text-sm text-neutral-700">Registered for GST</label>
            </div>
          )}

          {/* ── Trust / SMSF fields ─────────────────────────────────────── */}
          {showTrustFields && (
            <div className="rounded-xl border border-neutral-100 bg-neutral-50 p-4 space-y-3">
              <div className="text-xs font-semibold uppercase tracking-wide text-neutral-400">
                Trust / Fund details
              </div>
              {isTrust(et) && (
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    className="h-4 w-4 rounded border-neutral-300"
                    checked={draft.familyTrustElection ?? false}
                    onChange={(e) => set("familyTrustElection", e.target.checked)}
                  />
                  <span className="text-sm text-neutral-700">Family Trust Election lodged with ATO</span>
                </label>
              )}
              <div>
                <label className="block text-xs font-medium text-neutral-500 mb-1">Trust / fund name</label>
                <input
                  className="w-full rounded-xl border border-neutral-200 px-3 py-2 text-sm bg-white"
                  value={draft.trustName}
                  onChange={(e) => set("trustName", e.target.value)}
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-neutral-500 mb-1">Trustee name</label>
                  <input
                    className="w-full rounded-xl border border-neutral-200 px-3 py-2 text-sm bg-white"
                    value={draft.trusteeName}
                    onChange={(e) => set("trusteeName", e.target.value)}
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-neutral-500 mb-1">Trustee type</label>
                  <select
                    className="w-full rounded-xl border border-neutral-200 px-3 py-2 text-sm bg-white"
                    value={draft.trusteeType}
                    onChange={(e) => set("trusteeType", e.target.value)}
                  >
                    <option value="Individual">Individual</option>
                    <option value="Company">Company</option>
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-neutral-500 mb-1">Date established</label>
                  <input
                    type="date"
                    className="w-full rounded-xl border border-neutral-200 px-3 py-2 text-sm bg-white"
                    value={draft.dateEstablished ?? ""}
                    onChange={(e) => set("dateEstablished", e.target.value)}
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-neutral-500 mb-1">State of establishment</label>
                  <select
                    className="w-full rounded-xl border border-neutral-200 px-3 py-2 text-sm bg-white"
                    value={draft.stateOfEstablishment ?? ""}
                    onChange={(e) => set("stateOfEstablishment", e.target.value)}
                  >
                    <option value="">Select state</option>
                    {AU_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
              </div>
            </div>
          )}

          {/* ── Company fields ──────────────────────────────────────────── */}
          {showCompanyFields && (
            <div className="rounded-xl border border-neutral-100 bg-neutral-50 p-4 space-y-3">
              <div className="text-xs font-semibold uppercase tracking-wide text-neutral-400">
                Company details
              </div>
              <div>
                <label className="block text-xs font-medium text-neutral-500 mb-1">Company name</label>
                <input
                  className="w-full rounded-xl border border-neutral-200 px-3 py-2 text-sm bg-white"
                  value={draft.companyName}
                  onChange={(e) => set("companyName", e.target.value)}
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-neutral-500 mb-1">ACN</label>
                  <input
                    className="w-full rounded-xl border border-neutral-200 px-3 py-2 text-sm bg-white"
                    value={draft.acn ?? ""}
                    onChange={(e) => set("acn", e.target.value)}
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-neutral-500 mb-1">Registered office address</label>
                <input
                  className="w-full rounded-xl border border-neutral-200 px-3 py-2 text-sm bg-white"
                  value={draft.registeredOfficeAddress ?? ""}
                  onChange={(e) => set("registeredOfficeAddress", e.target.value)}
                />
              </div>
            </div>
          )}

          {/* ── SMSF member names ────────────────────────────────────────── */}
          {showSMSFMembers && (
            <div className="rounded-xl border border-neutral-100 bg-neutral-50 p-4 space-y-3">
              <div className="text-xs font-semibold uppercase tracking-wide text-neutral-400">
                Members
              </div>
              {memberNames.map((name, index) => (
                <div key={index} className="flex items-center gap-2">
                  <input
                    className="flex-1 rounded-xl border border-neutral-200 px-3 py-2 text-sm bg-white"
                    placeholder="Member name"
                    value={name}
                    onChange={(e) => updateMember(index, e.target.value)}
                  />
                  <button
                    onClick={() => removeMember(index)}
                    className="text-neutral-400 hover:text-red-500 px-2 text-lg"
                  >
                    ×
                  </button>
                </div>
              ))}
              <button
                onClick={addMember}
                className="text-sm text-blue-600 hover:underline"
              >
                + Add member
              </button>
            </div>
          )}

          {/* ── Beneficiaries ────────────────────────────────────────────── */}
          {showBeneficiaries && (
            <div className="rounded-xl border border-neutral-100 bg-neutral-50 p-4 space-y-3">
              <div className="flex items-center gap-1 text-xs font-semibold uppercase tracking-wide text-neutral-400">
                Beneficiaries
                <InfoTooltip tip="Each beneficiary's relationship to you, the primary account holder. 'Self' means you. Used for tax allocation grouping. Trustees retain full discretion over distributions regardless of this label. Beneficiaries added via 'Add from profile' read income + Medicare status live from My Profile — single source of truth, edit there to update everywhere. Manually-added beneficiaries (no profile link) keep their own income value." />
              </div>
              {beneficiaries.map((b) => (
                <div key={b.id} className="space-y-2 rounded-xl border border-neutral-100 bg-white p-3">
                  {/* Row 1: Name, relationship (or bucket badge), distribution %, notes, remove */}
                  <div className="grid grid-cols-12 gap-2 items-start">
                    <input
                      className="col-span-3 rounded-xl border border-neutral-200 px-3 py-2 text-sm bg-white"
                      placeholder="Name"
                      value={b.name}
                      onChange={(e) => updateBeneficiary(b.id, "name", e.target.value)}
                    />
                    {b.isBucketCompany ? (
                      <div className="col-span-2 flex items-center justify-center">
                        <span className="rounded-full bg-blue-100 px-2 py-1 text-xs font-semibold text-blue-700">
                          Corp.
                        </span>
                      </div>
                    ) : (
                      <select
                        className="col-span-2 rounded-xl border border-neutral-200 px-2 py-2 text-sm bg-white disabled:bg-neutral-100 disabled:text-neutral-500"
                        value={b.relationship}
                        // B119 sibling UX (Day 55) — primary user is always
                        // "Self" by definition; lock the select to prevent
                        // accidental change. Other rows render the full
                        // option list including a placeholder when value is "".
                        disabled={b.householdMemberId === "primary"}
                        title={b.householdMemberId === "primary"
                          ? "Primary user is always 'Self' — cannot be changed"
                          : undefined}
                        onChange={(e) => updateBeneficiary(b.id, "relationship", e.target.value)}
                      >
                        {b.relationship === "" && (
                          <option value="">— Pick relationship —</option>
                        )}
                        {RELATIONSHIP_OPTIONS.map((r) => <option key={r} value={r}>{r}</option>)}
                      </select>
                    )}
                    <div className="col-span-2 flex flex-col items-stretch gap-1">
                      <NumberInput
                        className="w-full rounded-xl border border-neutral-200 px-3 py-2 text-sm bg-white"
                        placeholder="%"
                        value={(() => {
                          const cur = currentFY();
                          const fyEntry = b.distributions?.find((d) => d.fy === cur);
                          if (fyEntry?.type === "percent") return fyEntry.value;
                          if (fyEntry?.type === "amount") return ""; // amount mode hides percent
                          return b.distributionPercent ?? ""; // legacy fallback
                        })()}
                        onChange={(e) => {
                          const cur = currentFY();
                          const newVal = e.target.value === "" ? undefined : Number(e.target.value);
                          const existing = b.distributions ?? [];
                          const idx = existing.findIndex((d) => d.fy === cur);
                          let next: BeneficiaryDistribution[];
                          if (newVal === undefined) {
                            next = idx >= 0 ? existing.filter((d) => d.fy !== cur) : existing;
                          } else if (idx >= 0) {
                            next = existing.map((d) => d.fy === cur ? { ...d, type: "percent" as const, value: newVal } : d);
                          } else {
                            next = [...existing, { fy: cur, type: "percent" as const, value: newVal }];
                          }
                          updateBeneficiary(b.id, "distributions", next as unknown as string);
                        }}
                      />
                      {b.distributions && priorFYs(b.distributions).length > 0 && (
                        <details className="text-[10px] text-neutral-500">
                          <summary className="cursor-pointer hover:text-neutral-800">
                            Prior FYs ({priorFYs(b.distributions).length})
                          </summary>
                          <ul className="mt-1 ml-2 space-y-0.5">
                            {priorFYs(b.distributions).map((d) => (
                              <li key={d.fy}>
                                {d.fy}: {d.type === "percent" ? `${d.value}%` : `$${d.value.toLocaleString()}`}
                                {d.actualDistributed !== undefined && ` (actual: $${d.actualDistributed.toLocaleString()})`}
                              </li>
                            ))}
                          </ul>
                        </details>
                      )}
                    </div>
                    <input
                      className="col-span-4 rounded-xl border border-neutral-200 px-3 py-2 text-sm bg-white"
                      placeholder="Notes"
                      value={b.notes ?? ""}
                      onChange={(e) => updateBeneficiary(b.id, "notes", e.target.value)}
                    />
                    <button
                      onClick={() => removeBeneficiary(b.id)}
                      className="col-span-1 text-neutral-400 hover:text-red-500 text-lg text-center"
                    >
                      ×
                    </button>
                  </div>
                  {/* Row 2: Annual income / Medicare (hidden for bucket companies)
                      B132 (Day 55): when the beneficiary is linked to a
                      household member (`householdMemberId` set), income +
                      Medicare are read live from profile via the resolver.
                      The form input + checkbox become read-only displays
                      with an "Edit in profile" affordance. Manual-add rows
                      (no link) keep editable inputs. */}
                  {!b.isBucketCompany && (() => {
                    const linked = isBeneficiaryLinked(b);
                    const resolvedIncome = profile
                      ? resolveBeneficiaryIncome(b, profile)
                      : (b.annualIncome ?? 0);
                    const resolvedMedicareExempt = profile
                      ? resolveBeneficiaryMedicareExempt(b, profile)
                      : (b.medicareExempt ?? false);
                    return (
                      <div className="grid grid-cols-12 gap-2 items-center">
                        <div className="col-span-3">
                          {linked ? (
                            <div
                              className="w-full rounded-xl border border-neutral-200 bg-neutral-50 px-3 py-2 text-sm text-neutral-700"
                              title="Income is read live from My Profile. Edit there to update everywhere."
                            >
                              {resolvedIncome > 0
                                ? `From profile: $${resolvedIncome.toLocaleString()}`
                                : "From profile: not set"}
                            </div>
                          ) : (
                            <NumberInput
                              className="w-full rounded-xl border border-neutral-200 px-3 py-2 text-sm bg-white"
                              placeholder="Annual income"
                              value={b.annualIncome === 0 || b.annualIncome === undefined ? "" : b.annualIncome}
                              onChange={(e) => updateBeneficiary(b.id, "annualIncome", parseInt(e.target.value, 10) || 0)}
                            />
                          )}
                        </div>
                        {linked ? (
                          <span
                            className="col-span-4 text-xs text-neutral-600"
                            title="Medicare exemption is read live from My Profile."
                          >
                            {resolvedMedicareExempt
                              ? "✓ Medicare exempt (from profile)"
                              : "Medicare applies (from profile)"}
                          </span>
                        ) : (
                          <label className="col-span-4 flex items-center gap-1.5 text-xs text-neutral-600 cursor-pointer">
                            <input
                              type="checkbox"
                              className="h-3.5 w-3.5"
                              checked={b.medicareExempt ?? false}
                              onChange={(e) => updateBeneficiary(b.id, "medicareExempt", e.target.checked)}
                            />
                            Medicare exempt
                          </label>
                        )}
                        {resolvedIncome > 0 && (
                          <>
                            <span className="col-span-2 rounded-full bg-neutral-100 px-2 py-0.5 text-xs text-neutral-600 text-center">
                              Marginal: {calculateMarginalRate(resolvedIncome)}%
                            </span>
                            <span className="col-span-3 rounded-full bg-neutral-100 px-2 py-0.5 text-xs text-neutral-600 text-center">
                              Effective: ~{calculateEffectiveRate(resolvedIncome, { medicareExempt: resolvedMedicareExempt }).toFixed(1)}%
                            </span>
                          </>
                        )}
                      </div>
                    );
                  })()}
                  {/* Row 3: Bucket company toggle */}
                  <div className="flex flex-wrap items-center gap-3 pt-1">
                    <label className="flex items-center gap-1.5 text-xs text-neutral-600 cursor-pointer">
                      <input
                        type="checkbox"
                        className="h-3.5 w-3.5"
                        checked={b.isBucketCompany ?? false}
                        onChange={(e) => updateBeneficiary(b.id, "isBucketCompany", e.target.checked)}
                      />
                      Bucket company (corporate beneficiary)
                    </label>
                    {b.isBucketCompany && (
                      <label className="flex items-center gap-1.5 text-xs text-neutral-600">
                        Tax rate:
                        <NumberInput
                          min={0}
                          max={30}
                          step={5}
                          value={b.bucketCompanyTaxRate ?? 30}
                          onChange={(e) => updateBeneficiary(b.id, "bucketCompanyTaxRate", parseInt(e.target.value, 10) || 30)}
                          className="w-12 rounded border border-neutral-200 bg-white px-1 py-0.5 text-right text-sm focus:outline-none"
                        />
                        %
                      </label>
                    )}
                  </div>
                  {b.isBucketCompany && (
                    <p className="text-xs text-neutral-400 leading-relaxed">
                      25% if the distributing trust runs a trading business (base rate entity). 30% for passive income trusts (rental income, investment distributions). When in doubt, use 30%. Confirm with your accountant.
                    </p>
                  )}
                  {b.isBucketCompany && (
                    <div className="rounded-lg border border-amber-200 bg-amber-50 px-2.5 py-2 text-xs text-amber-700 space-y-1">
                      <div>Bucket companies pay {b.bucketCompanyTaxRate ?? 30}% flat tax on all distributions. No CGT discount applies.</div>
                      <div className="text-amber-600">Division 7A: Cash must physically transfer to the bucket company bank account before the trust tax return is lodged.</div>
                    </div>
                  )}
                </div>
              ))}
              {beneficiaries.length > 0 && (
                <div className="text-xs text-neutral-400 grid grid-cols-12 gap-2">
                  <span className="col-span-5" />
                  <span className="col-span-2 font-medium text-neutral-600">
                    Total: {totalDistPct}%
                  </span>
                </div>
              )}
              {profilePeople.length > 0 ? (
                <div className="space-y-2">
                  <div className="flex flex-wrap gap-2">
                    <button
                      onClick={() => { setShowProfilePicker((v) => !v); setPickerValue(""); }}
                      className="text-sm text-blue-600 hover:underline"
                    >
                      + Add from profile
                    </button>
                    <span className="text-sm text-neutral-300">|</span>
                    <button
                      onClick={addBeneficiary}
                      className="text-sm text-neutral-500 hover:underline"
                    >
                      + Add manually
                    </button>
                  </div>
                  {showProfilePicker && (
                    <div className="flex items-center gap-2">
                      <select
                        value={pickerValue}
                        onChange={(e) => {
                          const person = profilePeople.find((p) => p.id === e.target.value);
                          if (person) addBeneficiaryFromProfile(person);
                        }}
                        className="flex-1 rounded-xl border border-neutral-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-neutral-300"
                      >
                        <option value="">— select person —</option>
                        {profilePeople.map((p) => (
                          <option key={p.id} value={p.id}>
                            {p.displayName}{p.income > 0 ? ` · $${p.income.toLocaleString()}/yr` : ""}
                          </option>
                        ))}
                      </select>
                      <button
                        onClick={() => { setShowProfilePicker(false); setPickerValue(""); }}
                        className="text-neutral-400 hover:text-neutral-600 px-1"
                      >
                        ✕
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <button
                  onClick={addBeneficiary}
                  className="text-sm text-blue-600 hover:underline"
                >
                  + Add beneficiary
                </button>
              )}
              <p className="text-xs text-neutral-400">
                Distribution percentages are for reference only — actual distributions are at trustee discretion.
              </p>
            </div>
          )}

          {/* ── B125: Trust loss carry-forward ledger (non-SMSF only) ─── */}
          {showBeneficiaries && draft.entityType !== "SMSF" && (
            <div className="rounded-xl border border-neutral-100 bg-neutral-50 p-4 space-y-3">
              <div className="flex items-center gap-1 text-xs font-semibold uppercase tracking-wide text-neutral-400">
                Tax loss carry-forward ledger ({(draft.lossCarryForward ?? []).length})
                <InfoTooltip tip="Per-FY trust tax losses (ITAA Div 36). Applied FIFO oldest-first against future positive net income before any beneficiary distribution. Edit the loss amount per FY; applied + remaining are computed automatically. Pick the FY of the loss from the dropdown — current FY excluded (current-year losses lodge at FY-end, not carried forward)." />
              </div>
              <p className="text-xs text-neutral-600">
                Per-FY tax losses (ITAA Div 36 / s.36-17 FIFO). Losses offset future positive net income before distributions. Edit the loss amount; <em>applied</em> and <em>remaining</em> are computed.
              </p>
              {(draft.lossCarryForward ?? []).map((entry, idx) => {
                const appliedTotal = Object.values(entry.applied ?? {}).reduce((s, v) => s + v, 0);
                const remainingComputed = Math.max(0, entry.taxLoss - appliedTotal);
                return (
                  <div key={idx} className="grid grid-cols-12 items-center gap-2 text-xs">
                    <select
                      className="col-span-2 rounded border border-neutral-200 bg-white px-2 py-1"
                      value={entry.fy}
                      onChange={(e) => {
                        const next = [...(draft.lossCarryForward ?? [])];
                        next[idx] = { ...entry, fy: e.target.value };
                        set("lossCarryForward", next);
                      }}
                    >
                      {entry.fy === "" && <option value="">— Pick FY —</option>}
                      {generateLedgerFYOptions(currentFY(), draft.lossCarryForward ?? []).map((o) => (
                        <option key={o.value} value={o.value} disabled={o.disabled && o.value !== entry.fy}>
                          {o.label}{o.disabled && o.value !== entry.fy ? " (used)" : ""}
                        </option>
                      ))}
                    </select>
                    <input
                      type="number"
                      placeholder="Tax loss $"
                      className="col-span-3 rounded border border-neutral-200 bg-white px-2 py-1"
                      value={entry.taxLoss === 0 ? "" : entry.taxLoss}
                      onChange={(e) => {
                        const next = [...(draft.lossCarryForward ?? [])];
                        const taxLoss = Math.max(0, Number(e.target.value) || 0);
                        next[idx] = { ...entry, taxLoss, remaining: Math.max(0, taxLoss - appliedTotal) };
                        set("lossCarryForward", next);
                      }}
                    />
                    <span className="col-span-3 text-neutral-600">
                      applied: ${appliedTotal.toLocaleString()}
                    </span>
                    <span className="col-span-3 text-neutral-600">
                      remaining: ${remainingComputed.toLocaleString()}
                    </span>
                    <button
                      type="button"
                      className="col-span-1 text-neutral-400 hover:text-rose-600 text-lg text-center"
                      onClick={() => {
                        const next = (draft.lossCarryForward ?? []).filter((_, i) => i !== idx);
                        set("lossCarryForward", next);
                      }}
                    >
                      ×
                    </button>
                  </div>
                );
              })}
              <button
                type="button"
                className="text-sm text-blue-600 hover:underline"
                onClick={() => {
                  const next = [...(draft.lossCarryForward ?? []), emptyLedgerEntry("")];
                  set("lossCarryForward", next);
                }}
              >
                + Add loss FY
              </button>
            </div>
          )}

          {/* ── Documents ────────────────────────────────────────────────── */}
          <div className="rounded-xl border border-neutral-100 bg-neutral-50 p-4 space-y-3">
            <div className="text-xs font-semibold uppercase tracking-wide text-neutral-400">
              Documents
            </div>
            {docUploadError && (
              <div className="rounded-lg bg-red-50 px-3 py-2 text-xs text-red-600">{docUploadError}</div>
            )}
            {DOCUMENT_LABELS.map((label) => {
              const existing = documents.filter((d) => d.label === label);
              const slotUploading = docUploading === `new:${label}`;
              return (
                <div key={label} className="space-y-1">
                  <div className="text-xs text-neutral-500">{label}</div>
                  {existing.map((d) => {
                    const lost = d.needsReupload || (!d.filePath && !!d.filename);
                    const docUploadingThis = docUploading === d.id;
                    return (
                      <div
                        key={d.id}
                        className={`flex items-center justify-between rounded-lg border px-3 py-1.5 text-xs ${lost ? "border-amber-300 bg-amber-50" : "border-neutral-200 bg-white"}`}
                      >
                        <div className="min-w-0 flex-1">
                          {lost ? (
                            <div className="text-amber-700">
                              <div className="truncate font-medium">{d.filename}</div>
                              <div className="text-[11px]">File lost — please re-upload</div>
                            </div>
                          ) : d.filePath ? (
                            <button
                              onClick={() => downloadFromStorage("profile-documents", d.filePath!)}
                              className="truncate text-blue-600 hover:underline"
                            >
                              {d.filename}
                            </button>
                          ) : (
                            <span className="truncate text-neutral-600">{d.filename}</span>
                          )}
                        </div>
                        <div className="ml-2 flex shrink-0 items-center gap-1">
                          <label className="cursor-pointer rounded border border-dashed border-neutral-300 px-2 py-0.5 text-[11px] text-neutral-500 hover:bg-neutral-50">
                            {docUploadingThis ? "Uploading…" : lost ? "Re-upload" : "Replace"}
                            <input
                              type="file"
                              className="hidden"
                              disabled={docUploadingThis}
                              onChange={(e) => {
                                const f = e.target.files?.[0];
                                if (f) void uploadStructureDoc(label, f, d.id);
                                e.target.value = "";
                              }}
                            />
                          </label>
                          <button
                            onClick={() => { if (confirm("Remove this document?")) void removeStructureDoc(d.id); }}
                            className="text-neutral-300 hover:text-red-500 text-base leading-none"
                            title="Remove"
                          >
                            ×
                          </button>
                        </div>
                      </div>
                    );
                  })}
                  <label className="inline-block cursor-pointer rounded-lg border border-dashed border-neutral-300 px-3 py-1.5 text-xs text-neutral-500 hover:bg-white">
                    {slotUploading ? "Uploading…" : "+ Upload"}
                    <input
                      type="file"
                      className="hidden"
                      disabled={slotUploading}
                      onChange={(e) => {
                        const f = e.target.files?.[0];
                        if (f) void uploadStructureDoc(label, f);
                        e.target.value = "";
                      }}
                    />
                  </label>
                </div>
              );
            })}
          </div>
        </div>

        {/* ── Transaction account ─────────────────────────────────────────── */}
        <div className="mt-6 space-y-3">
          <h3 className="text-sm font-semibold text-neutral-700">Transaction account</h3>
          <p className="text-xs text-neutral-400">Where you transfer funds to each pay cycle for this entity.</p>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] font-medium uppercase tracking-wide text-neutral-400">Bank / Institution</label>
              <input className="mt-1 w-full rounded-lg border border-neutral-200 px-3 py-1.5 text-sm" value={draft.transactionBankName ?? ""} onChange={(e) => set("transactionBankName", e.target.value)} />
            </div>
            <div>
              <label className="text-[10px] font-medium uppercase tracking-wide text-neutral-400">BSB</label>
              <input className="mt-1 w-full rounded-lg border border-neutral-200 px-3 py-1.5 text-sm" value={draft.transactionBSB ?? ""} onChange={(e) => set("transactionBSB", e.target.value)} />
            </div>
            <div>
              <label className="text-[10px] font-medium uppercase tracking-wide text-neutral-400">Account name</label>
              <input className="mt-1 w-full rounded-lg border border-neutral-200 px-3 py-1.5 text-sm" value={draft.transactionAccountName ?? ""} onChange={(e) => set("transactionAccountName", e.target.value)} />
            </div>
            <div>
              <label className="text-[10px] font-medium uppercase tracking-wide text-neutral-400">Account number</label>
              <input className="mt-1 w-full rounded-lg border border-neutral-200 px-3 py-1.5 text-sm" value={draft.transactionAccountNumber ?? ""} onChange={(e) => set("transactionAccountNumber", e.target.value)} />
            </div>
          </div>
        </div>

        {/* ── Footer ──────────────────────────────────────────────────────── */}
        <div className="mt-6 flex gap-3">
          <button
            onClick={handleSave}
            disabled={!canSave}
            className="rounded-xl bg-neutral-900 px-5 py-2 text-sm font-medium text-white hover:bg-neutral-700 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            Save
          </button>
          <button
            onClick={onClose}
            className="rounded-xl border border-neutral-200 px-5 py-2 text-sm text-neutral-700 hover:bg-neutral-50"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}
